import { z } from 'zod';
import { AssetTagSchema, type AssetTagType } from './generated';

export const ProtocolAssetTagSchema = z.enum([
  'REST',
  'GRAPHQL',
  'SOAP',
  'AI',
  'GRPC',
]);

export type ProtocolAssetTagType = z.infer<typeof ProtocolAssetTagSchema>;

export const TRANSPORT_ASSET_TAGS = [
  'WEBSOCKET',
] as const satisfies readonly AssetTagType[];

export type TransportAssetTagType = (typeof TRANSPORT_ASSET_TAGS)[number];

export const FEATURE_ASSET_TAGS = [
  'LOGIN',
  'MFA',
  'RESET_PASSWORD',
  'OUT_OF_SCOPE',
  'CRITICAL_TO_BUSINESS',
  'BASIC_AUTH',
  'USER_PERMISSIONS_ROLES',
  'FILE_UPLOAD',
] as const satisfies readonly AssetTagType[];

export type FeatureAssetTagType = (typeof FEATURE_ASSET_TAGS)[number];

export const getActiveFeatureTags = ({
  assetTags,
}: {
  assetTags: readonly string[] | null | undefined;
}): FeatureAssetTagType[] =>
  FEATURE_ASSET_TAGS.filter((tag) => (assetTags ?? []).includes(tag));

const MANUAL_ONLY_FEATURE_TAG_SET = new Set<string>([
  'OUT_OF_SCOPE',
  'CRITICAL_TO_BUSINESS',
] as const satisfies readonly FeatureAssetTagType[]);

export const getAutoDisplayFeatureTags = ({
  assetTags,
}: {
  assetTags: readonly string[] | null | undefined;
}): FeatureAssetTagType[] =>
  getActiveFeatureTags({ assetTags }).filter(
    (tag) => !MANUAL_ONLY_FEATURE_TAG_SET.has(tag),
  );

export const FEATURE_ASSET_TAG_SET = new Set<string>(FEATURE_ASSET_TAGS);

export const TRANSPORT_ASSET_TAG_SET = new Set<string>(TRANSPORT_ASSET_TAGS);

export const isProtocolAssetTag = (tag: string): tag is ProtocolAssetTagType =>
  ProtocolAssetTagSchema.safeParse(tag).success;

export const isFeatureAssetTag = (tag: string): tag is FeatureAssetTagType =>
  FEATURE_ASSET_TAG_SET.has(tag);

export const isTransportAssetTag = (
  tag: string,
): tag is TransportAssetTagType => TRANSPORT_ASSET_TAG_SET.has(tag);

export const getActiveTransportTags = ({
  assetTags,
}: {
  assetTags: readonly string[] | null | undefined;
}): TransportAssetTagType[] =>
  TRANSPORT_ASSET_TAGS.filter((tag) => (assetTags ?? []).includes(tag));

export const isWebSocketAssetTags = (
  assetTags: readonly string[] | null | undefined,
): boolean => (assetTags ?? []).includes('WEBSOCKET');

export const isGrpcAssetTags = (
  assetTags: readonly string[] | null | undefined,
): boolean => (assetTags ?? []).includes('GRPC');

export const hasAutoSemanticFeatureTags = (
  assetTags: readonly string[] | null | undefined,
): boolean =>
  (assetTags ?? []).some(
    (tag) => isFeatureAssetTag(tag) && !MANUAL_ONLY_FEATURE_TAG_SET.has(tag),
  );

export const isAiAssetTags = (
  assetTags: readonly string[] | null | undefined,
): boolean => (assetTags ?? []).includes('AI');

export const getProtocolFromAssetTags = ({
  assetTags,
}: {
  assetTags: readonly string[] | null | undefined;
}): ProtocolAssetTagType | null => {
  for (const tag of assetTags ?? []) {
    if (isProtocolAssetTag(tag)) {
      return tag;
    }
  }

  return null;
};

export const getProtocolFromHarVerification = ({
  graphqlOperationName,
  soapOperationName,
  grpcServiceName,
  grpcMethodName,
  bodyType,
}: {
  graphqlOperationName?: string | null;
  soapOperationName?: string | null;
  grpcServiceName?: string | null;
  grpcMethodName?: string | null;
  bodyType?: string | null;
}): ProtocolAssetTagType | null => {
  if (graphqlOperationName?.trim()) {
    return 'GRAPHQL';
  }

  if (soapOperationName?.trim()) {
    return 'SOAP';
  }

  if (grpcServiceName?.trim() || grpcMethodName?.trim()) {
    return 'GRPC';
  }

  if (bodyType === 'GRAPHQL') {
    return 'GRAPHQL';
  }

  if (bodyType === 'SOAP') {
    return 'SOAP';
  }

  if (bodyType === 'PROTOBUF') {
    return 'GRPC';
  }

  return null;
};

export const resolveEndpointProtocol = ({
  llmAssetTags,
  graphqlOperationName,
  soapOperationName,
  grpcServiceName,
  grpcMethodName,
  bodyType,
}: {
  llmAssetTags?: readonly string[] | null;
  graphqlOperationName?: string | null;
  soapOperationName?: string | null;
  grpcServiceName?: string | null;
  grpcMethodName?: string | null;
  bodyType?: string | null;
}): ProtocolAssetTagType | null =>
  getProtocolFromAssetTags({ assetTags: llmAssetTags }) ??
  getProtocolFromHarVerification({
    graphqlOperationName,
    soapOperationName,
    grpcServiceName,
    grpcMethodName,
    bodyType,
  });

export const composeAssetTags = ({
  protocol,
  featureTags,
  transportTags = [],
}: {
  protocol: ProtocolAssetTagType | null;
  featureTags: readonly AssetTagType[];
  transportTags?: readonly TransportAssetTagType[];
}): AssetTagType[] => {
  const combined: AssetTagType[] = [];

  if (protocol) {
    combined.push(protocol);
  }

  for (const tag of transportTags) {
    if (!combined.includes(tag)) {
      combined.push(tag);
    }
  }

  for (const tag of featureTags) {
    if (!combined.includes(tag)) {
      combined.push(tag);
    }
  }

  return combined;
};

export const mergeAssetTagsPreservingProtocol = ({
  existingAssetTags,
  nextFeatureTags,
  nextProtocol,
  nextTransportTags,
}: {
  existingAssetTags: readonly AssetTagType[] | null | undefined;
  nextFeatureTags: readonly AssetTagType[];
  nextProtocol: ProtocolAssetTagType | null;
  nextTransportTags?: readonly TransportAssetTagType[];
}): AssetTagType[] => {
  const existingProtocol =
    getProtocolFromAssetTags({ assetTags: existingAssetTags }) ?? nextProtocol;
  const protocol = nextProtocol ?? existingProtocol ?? 'REST';
  const preservedFeatures = (existingAssetTags ?? []).filter(isFeatureAssetTag);
  const preservedTransport = (existingAssetTags ?? []).filter(
    isTransportAssetTag,
  );

  return composeAssetTags({
    protocol,
    transportTags: [...preservedTransport, ...(nextTransportTags ?? [])],
    featureTags: [...preservedFeatures, ...nextFeatureTags],
  });
};

export const hasAiMembershipChanged = ({
  previousAssetTags,
  nextAssetTags,
}: {
  previousAssetTags: readonly string[] | null | undefined;
  nextAssetTags: readonly string[] | null | undefined;
}): boolean =>
  isAiAssetTags(previousAssetTags) !== isAiAssetTags(nextAssetTags);

export { AssetTagSchema, type AssetTagType };
